/**
 * Quality Dashboard Component
 * Displays quality assessment results for domain pack research
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Info,
  Loader2,
  Play,
  XCircle,
  ThumbsUp,
  ThumbsDown,
  ClipboardCheck,
  Lightbulb,
  Tag,
} from 'lucide-react';
import { getDomainPackQuality, assessDomainPackQuality, getAssessmentJobStatus } from '../../lib/api/domainPacksApi';
import ReviewModeSelector from './ReviewModeSelector';
import QuickReviewInterface from './QuickReviewInterface';
import FullReviewInterface from './FullReviewInterface';
import TrustAIReview from './TrustAIReview';

interface QualityDashboardProps {
  domainPackId: string;
  domainName: string;
}

const QualityDashboard: React.FC<QualityDashboardProps> = ({ domainPackId, domainName }) => {
  const queryClient = useQueryClient();
  const [isAssessing, setIsAssessing] = useState(false);
  const [assessmentJobId, setAssessmentJobId] = useState<string | null>(null);
  const [assessmentProgress, setAssessmentProgress] = useState<any>(null);
  const [showReviewModeSelector, setShowReviewModeSelector] = useState(false);
  const [reviewMode, setReviewMode] = useState<'quick' | 'full' | 'trust_ai' | null>(null);
  const [reviewerName] = useState('Current User'); // TODO: Get from auth context
  const [reviewerEmail] = useState('user@example.com'); // TODO: Get from auth context

  // Fetch quality data
  const {
    data: qualityData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['domain-pack-quality', domainPackId],
    queryFn: () => getDomainPackQuality(domainPackId),
    enabled: !!domainPackId,
  });

  // Poll for assessment job status
  const { data: jobStatus } = useQuery({
    queryKey: ['assessment-job-status', domainPackId, assessmentJobId],
    queryFn: () => getAssessmentJobStatus(domainPackId, assessmentJobId!),
    enabled: !!assessmentJobId && isAssessing,
    refetchInterval: (query) => {
      // Stop polling if job is completed or failed (React Query v5 pattern)
      const queryData = query.state.data;
      if (queryData?.status === 'completed' || queryData?.status === 'failed') {
        return false;
      }
      // Poll every 10 seconds while in progress
      return 10000;
    },
  });

  // Update progress when job status changes
  React.useEffect(() => {
    if (jobStatus) {
      setAssessmentProgress(jobStatus);

      // If job completed successfully
      if (jobStatus.status === 'completed') {
        setIsAssessing(false);
        setAssessmentJobId(null);
        queryClient.invalidateQueries({ queryKey: ['domain-pack-quality', domainPackId] });
        refetch();
      }

      // If job failed
      if (jobStatus.status === 'failed') {
        setIsAssessing(false);
        setAssessmentJobId(null);
        alert(`Assessment failed: ${jobStatus.error || 'Unknown error'}`);
      }
    }
  }, [jobStatus, domainPackId, queryClient, refetch]);

  // Trigger assessment mutation
  const assessMutation = useMutation({
    mutationFn: () => assessDomainPackQuality(domainPackId),
    onSuccess: (data) => {
      // Start polling for job status
      setAssessmentJobId(data.jobId);
      setIsAssessing(true);
      setAssessmentProgress({
        status: 'queued',
        totalSubdomains: data.totalSubdomains,
        estimatedDuration: data.estimatedDuration,
        progress: {
          totalSubdomains: data.totalSubdomains,
          completedSubdomains: 0,
          failedSubdomains: 0,
          percentComplete: 0,
        },
      });
    },
    onError: (error: any) => {
      console.error('Failed to start assessment:', error);
      setIsAssessing(false);
      alert(`Failed to start assessment: ${error.message || 'Unknown error'}`);
    },
  });

  const handleRunAssessment = () => {
    if (confirm('Run quality assessment? This will take 2-3 minutes per subdomain.')) {
      assessMutation.mutate();
    }
  };

  const handleModeSelected = (mode: 'quick' | 'full' | 'trust_ai') => {
    setReviewMode(mode);
  };

  const handleReviewComplete = () => {
    setReviewMode(null);
    refetch(); // Refresh quality data
  };

  const handleReviewCancel = () => {
    setReviewMode(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-red-900 mb-1">
              Failed to Load Quality Data
            </h3>
            <p className="text-sm text-red-700">
              {(error as any).message || 'An unknown error occurred'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Not assessed yet
  if (!qualityData?.assessed) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-8">
        <div className="text-center max-w-xl mx-auto">
          <Info className="w-16 h-16 text-primary-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-neutral-900 mb-2">
            Quality Assessment Not Run
          </h3>
          <p className="text-neutral-600 mb-6">
            Run AI quality assessment to evaluate research across 5 dimensions:
            Completeness, Accuracy, Relevance, Actionability, and Recency.
          </p>

          {/* Progress display while assessing */}
          {isAssessing && assessmentProgress && (
            <div className="mb-6 bg-primary-50 border border-primary-200 rounded-lg p-6 text-left">
              <div className="flex items-start gap-3">
                <Loader2 className="w-6 h-6 text-primary-600 animate-spin flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-primary-900 mb-2">
                    Quality Assessment in Progress
                  </h4>
                  <p className="text-sm text-primary-700 mb-4">
                    Estimated duration: {assessmentProgress.estimatedDuration || '15-20 minutes'}
                  </p>

                  {/* Progress bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-primary-700 mb-1">
                      <span>Progress</span>
                      <span>
                        {assessmentProgress.progress?.completedSubdomains || 0} / {assessmentProgress.progress?.totalSubdomains || 0} subdomains
                      </span>
                    </div>
                    <div className="w-full bg-primary-100 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${assessmentProgress.progress?.percentComplete || 0}%` }}
                      />
                    </div>
                  </div>

                  {/* Subdomain status list */}
                  {assessmentProgress.subdomainTasks && assessmentProgress.subdomainTasks.length > 0 && (
                    <div className="space-y-2">
                      {assessmentProgress.subdomainTasks.map((task: any) => (
                        <div key={task.subdomainId} className="flex items-center gap-2 text-sm">
                          {task.status === 'completed' && (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          )}
                          {task.status === 'in_progress' && (
                            <Loader2 className="w-4 h-4 text-primary-600 animate-spin" />
                          )}
                          {task.status === 'failed' && (
                            <AlertCircle className="w-4 h-4 text-red-600" />
                          )}
                          {task.status === 'pending' && (
                            <div className="w-4 h-4 rounded-full border-2 border-neutral-300" />
                          )}
                          <span className={task.status === 'completed' ? 'text-green-700' : 'text-neutral-700'}>
                            {task.subdomainName}
                            {task.overallScore && ` - ${task.overallScore}/100 (${task.overallRating})`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleRunAssessment}
            disabled={isAssessing}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isAssessing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Assessment Running...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Run Quality Assessment
              </>
            )}
          </button>
          {qualityData && (
            <p className="mt-4 text-sm text-neutral-500">
              {qualityData.total_subdomains} subdomain{qualityData.total_subdomains !== 1 ? 's' : ''} to assess
            </p>
          )}
        </div>
      </div>
    );
  }

  // Rating badge component
  const RatingBadge: React.FC<{ rating: string; score: number }> = ({ rating, score }) => {
    const colors = {
      excellent: 'bg-green-100 text-green-800 border-green-200',
      good: 'bg-blue-100 text-blue-800 border-blue-200',
      fair: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      poor: 'bg-red-100 text-red-800 border-red-200',
    };

    return (
      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${colors[rating as keyof typeof colors] || colors.fair}`}>
        <span className="text-2xl font-bold">{score}/100</span>
        <span className="text-sm font-medium uppercase">{rating}</span>
      </div>
    );
  };

  // Severity badge
  const SeverityBadge: React.FC<{ severity: 'critical' | 'major' | 'minor'; count: number }> = ({ severity, count }) => {
    const config = {
      critical: { label: 'Critical', icon: XCircle, color: 'bg-red-100 text-red-700 border-red-200' },
      major: { label: 'Major', icon: AlertTriangle, color: 'bg-orange-100 text-orange-700 border-orange-200' },
      minor: { label: 'Minor', icon: AlertCircle, color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
    };

    const { label, icon: Icon, color } = config[severity];

    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${color}`}>
        <Icon className="w-4 h-4" />
        <span className="font-semibold">{count}</span>
        <span className="text-sm">{label}</span>
      </div>
    );
  };

  // Progress bar component
  const DimensionBar: React.FC<{ label: string; score: number }> = ({ label, score }) => {
    const getColor = (score: number) => {
      if (score >= 90) return 'bg-green-600';
      if (score >= 75) return 'bg-blue-600';
      if (score >= 60) return 'bg-yellow-600';
      return 'bg-red-600';
    };

    const getRating = (score: number) => {
      if (score >= 90) return 'Excellent';
      if (score >= 75) return 'Good';
      if (score >= 60) return 'Fair';
      return 'Poor';
    };

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-neutral-700">{label}</span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-neutral-900">{score}/100</span>
            <span className="text-xs text-neutral-600">{getRating(score)}</span>
          </div>
        </div>
        <div className="w-full h-3 bg-neutral-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${getColor(score)} transition-all duration-300`}
            style={{ width: `${score}%` }}
          />
        </div>
      </div>
    );
  };

  // If review mode is selected, show review interface
  if (reviewMode === 'quick') {
    return (
      <QuickReviewInterface
        domainPackId={domainPackId}
        domainName={domainName}
        reviewerName={reviewerName}
        reviewerEmail={reviewerEmail}
        onComplete={handleReviewComplete}
        onCancel={handleReviewCancel}
      />
    );
  }

  if (reviewMode === 'full') {
    return (
      <FullReviewInterface
        domainPackId={domainPackId}
        domainName={domainName}
        reviewerName={reviewerName}
        reviewerEmail={reviewerEmail}
        onComplete={handleReviewComplete}
        onCancel={handleReviewCancel}
      />
    );
  }

  if (reviewMode === 'trust_ai') {
    return (
      <TrustAIReview
        domainPackId={domainPackId}
        domainName={domainName}
        reviewerName={reviewerName}
        reviewerEmail={reviewerEmail}
        qualitySummary={qualityData}
        onComplete={handleReviewComplete}
        onCancel={handleReviewCancel}
        onSwitchMode={handleModeSelected}
      />
    );
  }

  return (
    <>
      {/* Review Mode Selector Modal */}
      <ReviewModeSelector
        isOpen={showReviewModeSelector}
        onClose={() => setShowReviewModeSelector(false)}
        domainPackId={domainPackId}
        domainName={domainName}
        qualitySummary={qualityData || { total_subdomains: 0 }}
        onModeSelected={handleModeSelected}
      />

      <div className="space-y-6">
      {/* Header with Overall Score */}
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900 mb-1">Quality Assessment</h2>
            <p className="text-neutral-600">{domainName}</p>
          </div>
          <RatingBadge rating={qualityData.overall_rating!} score={Math.round(qualityData.overall_score!)} />
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-neutral-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-neutral-900">{qualityData.total_subdomains}</div>
            <div className="text-sm text-neutral-600">Total Subdomains</div>
          </div>
          <div className="bg-neutral-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-neutral-900">{qualityData.assessed_subdomains}</div>
            <div className="text-sm text-neutral-600">Assessed</div>
          </div>
          <div className="bg-neutral-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-neutral-900">{qualityData.total_issues || 0}</div>
            <div className="text-sm text-neutral-600">Total Issues</div>
          </div>
          <div className="bg-neutral-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">
              {qualityData.subdomain_breakdown?.excellent || 0}
            </div>
            <div className="text-sm text-neutral-600">Excellent</div>
          </div>
        </div>

        {/* Re-assess and Review buttons */}
        <div className="mt-6 pt-6 border-t border-neutral-200 flex items-center gap-3">
          <button
            onClick={handleRunAssessment}
            disabled={isAssessing}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isAssessing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Re-assessing...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Re-run Assessment
              </>
            )}
          </button>

          <button
            onClick={() => setShowReviewModeSelector(true)}
            disabled={isAssessing}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ClipboardCheck className="w-4 h-4" />
            Start Review
          </button>
        </div>
      </div>

      {/* Subdomain Breakdown */}
      {qualityData.subdomain_breakdown && (
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">Subdomain Quality Breakdown</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-green-600 rounded-full"></div>
              <div>
                <div className="text-2xl font-bold text-neutral-900">{qualityData.subdomain_breakdown.excellent}</div>
                <div className="text-sm text-neutral-600">Excellent (≥90)</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
              <div>
                <div className="text-2xl font-bold text-neutral-900">{qualityData.subdomain_breakdown.good}</div>
                <div className="text-sm text-neutral-600">Good (75-89)</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-yellow-600 rounded-full"></div>
              <div>
                <div className="text-2xl font-bold text-neutral-900">{qualityData.subdomain_breakdown.fair}</div>
                <div className="text-sm text-neutral-600">Fair (60-74)</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-red-600 rounded-full"></div>
              <div>
                <div className="text-2xl font-bold text-neutral-900">{qualityData.subdomain_breakdown.poor}</div>
                <div className="text-sm text-neutral-600">Poor (&lt;60)</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Issues Summary */}
      {qualityData.issues_by_severity && qualityData.total_issues! > 0 && (
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">Issues Found</h3>
          <div className="flex flex-wrap gap-3">
            {qualityData.issues_by_severity.critical > 0 && (
              <SeverityBadge severity="critical" count={qualityData.issues_by_severity.critical} />
            )}
            {qualityData.issues_by_severity.major > 0 && (
              <SeverityBadge severity="major" count={qualityData.issues_by_severity.major} />
            )}
            {qualityData.issues_by_severity.minor > 0 && (
              <SeverityBadge severity="minor" count={qualityData.issues_by_severity.minor} />
            )}
          </div>
          {qualityData.issues_by_severity.critical > 0 && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Action Required:</strong> {qualityData.issues_by_severity.critical} critical issue
                {qualityData.issues_by_severity.critical !== 1 ? 's' : ''} need immediate attention. Consider re-researching affected subdomains.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Detailed Issues Section */}
      {qualityData.subdomain_assessments && qualityData.subdomain_assessments.length > 0 && (() => {
        // Extract and flatten all issues from subdomain assessments
        const allIssues = qualityData.subdomain_assessments
          .filter((assessment: any) => assessment.issues && assessment.issues.length > 0)
          .flatMap((assessment: any) =>
            assessment.issues.map((issue: any) => ({
              ...issue,
              subdomainName: assessment.subdomainName,
              subdomainId: assessment.subdomainId,
            }))
          );

        // Group issues by severity
        const criticalIssues = allIssues.filter((issue: any) => issue.severity === 'critical');
        const majorIssues = allIssues.filter((issue: any) => issue.severity === 'major');
        const minorIssues = allIssues.filter((issue: any) => issue.severity === 'minor');

        const IssueCard: React.FC<{ issue: any }> = ({ issue }) => {
          const [isExpanded, setIsExpanded] = React.useState(false);

          const severityConfig = {
            critical: {
              color: 'border-red-300 bg-red-50',
              badge: 'bg-red-100 text-red-800 border-red-300',
              icon: XCircle,
              iconColor: 'text-red-600'
            },
            major: {
              color: 'border-orange-300 bg-orange-50',
              badge: 'bg-orange-100 text-orange-800 border-orange-300',
              icon: AlertTriangle,
              iconColor: 'text-orange-600'
            },
            minor: {
              color: 'border-yellow-300 bg-yellow-50',
              badge: 'bg-yellow-100 text-yellow-800 border-yellow-300',
              icon: AlertCircle,
              iconColor: 'text-yellow-600'
            },
          };

          const config = severityConfig[issue.severity as keyof typeof severityConfig];
          const Icon = config.icon;

          const dimensionBadgeColors: Record<string, string> = {
            completeness: 'bg-blue-100 text-blue-800 border-blue-200',
            accuracy: 'bg-green-100 text-green-800 border-green-200',
            relevance: 'bg-purple-100 text-purple-800 border-purple-200',
            actionability: 'bg-indigo-100 text-indigo-800 border-indigo-200',
            recency: 'bg-pink-100 text-pink-800 border-pink-200',
          };

          return (
            <div className={`border rounded-lg p-4 ${config.color} transition-all`}>
              {/* Header */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-start gap-3 flex-1">
                  <Icon className={`w-5 h-5 ${config.iconColor} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-neutral-900">{issue.title}</h4>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${config.badge}`}>
                        {issue.severity.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-neutral-600">
                      {issue.subdomainName} • <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${dimensionBadgeColors[issue.dimension] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                        {issue.dimension}
                      </span>
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-neutral-500 hover:text-neutral-700 transition-colors"
                >
                  {isExpanded ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Description (always visible) */}
              <p className="text-sm text-neutral-700 mb-3 pl-8">{issue.description}</p>

              {/* Expandable Details */}
              {isExpanded && (
                <div className="pl-8 space-y-3 pt-3 border-t border-neutral-300">
                  {/* Affected Items */}
                  {issue.affectedItems && issue.affectedItems.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium text-neutral-700 mb-1">
                        <Tag className="w-4 h-4" />
                        <span>Affected Items ({issue.affectedItems.length})</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {issue.affectedItems.map((item: string, idx: number) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-neutral-300 rounded text-xs"
                          >
                            {issue.affectedTypes && issue.affectedTypes[idx] && (
                              <span className="text-neutral-500">{issue.affectedTypes[idx]}:</span>
                            )}
                            <span className="font-mono">{item}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommendation */}
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium text-neutral-700 mb-1">
                      <Lightbulb className="w-4 h-4" />
                      <span>Recommendation</span>
                    </div>
                    <p className="text-sm text-neutral-700 bg-white border border-neutral-300 rounded p-3">
                      {issue.recommendation}
                    </p>
                  </div>

                  {/* Issue Metadata */}
                  <div className="text-xs text-neutral-500 flex gap-4">
                    <span>Category: {issue.category?.replace('_', ' ')}</span>
                    <span>Status: {issue.status || 'open'}</span>
                  </div>
                </div>
              )}
            </div>
          );
        };

        if (allIssues.length === 0) return null;

        return (
          <div className="bg-white rounded-lg border border-neutral-200 p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Issue Details</h3>

            <div className="space-y-6">
              {/* Critical Issues */}
              {criticalIssues.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <XCircle className="w-5 h-5 text-red-600" />
                    <h4 className="font-semibold text-neutral-900">
                      Critical Issues ({criticalIssues.length})
                    </h4>
                  </div>
                  <div className="space-y-3">
                    {criticalIssues.map((issue: any) => (
                      <IssueCard key={issue.issueId} issue={issue} />
                    ))}
                  </div>
                </div>
              )}

              {/* Major Issues */}
              {majorIssues.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                    <h4 className="font-semibold text-neutral-900">
                      Major Issues ({majorIssues.length})
                    </h4>
                  </div>
                  <div className="space-y-3">
                    {majorIssues.map((issue: any) => (
                      <IssueCard key={issue.issueId} issue={issue} />
                    ))}
                  </div>
                </div>
              )}

              {/* Minor Issues */}
              {minorIssues.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                    <h4 className="font-semibold text-neutral-900">
                      Minor Issues ({minorIssues.length})
                    </h4>
                  </div>
                  <div className="space-y-3">
                    {minorIssues.map((issue: any) => (
                      <IssueCard key={issue.issueId} issue={issue} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Subdomain Details */}
      {qualityData.subdomain_assessments && qualityData.subdomain_assessments.length > 0 && (
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">Subdomain Assessments</h3>
          <div className="space-y-4">
            {qualityData.subdomain_assessments
              .filter((assessment: any) => assessment.overallScore !== undefined)
              .sort((a: any, b: any) => b.overallScore - a.overallScore)
              .map((assessment: any) => (
                <div
                  key={assessment.subdomainId}
                  className="border border-neutral-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-neutral-900">{assessment.subdomainName}</h4>
                    <RatingBadge rating={assessment.overallRating} score={Math.round(assessment.overallScore)} />
                  </div>

                  {/* 5 Dimensions */}
                  <div className="space-y-3">
                    <DimensionBar label="Completeness" score={Math.round(assessment.completenessScore)} />
                    <DimensionBar label="Accuracy" score={Math.round(assessment.accuracyScore)} />
                    <DimensionBar label="Relevance" score={Math.round(assessment.relevanceScore)} />
                    <DimensionBar label="Actionability" score={Math.round(assessment.actionabilityScore)} />
                    <DimensionBar label="Recency" score={Math.round(assessment.recencyScore)} />
                  </div>

                  {/* Issues count */}
                  {(assessment.criticalIssuesCount > 0 ||
                    assessment.majorIssuesCount > 0 ||
                    assessment.minorIssuesCount > 0) && (
                    <div className="mt-3 pt-3 border-t border-neutral-200 flex gap-2 text-sm">
                      {assessment.criticalIssuesCount > 0 && (
                        <span className="text-red-600">
                          {assessment.criticalIssuesCount} critical
                        </span>
                      )}
                      {assessment.majorIssuesCount > 0 && (
                        <span className="text-orange-600">
                          {assessment.majorIssuesCount} major
                        </span>
                      )}
                      {assessment.minorIssuesCount > 0 && (
                        <span className="text-yellow-600">
                          {assessment.minorIssuesCount} minor
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default QualityDashboard;
